import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useInvitation } from '../contexts/InvitationContext';
import { useToast } from '../contexts/ToastContext';
import { familyAPI } from '../services/api';
import InvitationWelcome from './InvitationWelcome';
import FamilyConflict from './FamilyConflict';

/**
 * InvitationRouter
 *
 * Replaces the old "always show InvitationWelcome" routing. When the user
 * clicks an invite email link, the URL lands here. We pre-flight the
 * token against the backend, get back a `destination`, and either render
 * the right surface inline or react-router-navigate to it.
 *
 * Destinations:
 *   signup                        → /signup (with banner)
 *   login                         → /login  (with banner)
 *   login_for_existing_member     → /login  (banner: "Sign in to your X family account")
 *   login_then_family_conflict    → /login  (we'll detect the conflict post-login)
 *   auto_accept                   → POST /families/invitations/:token/accept then route
 *   invitation_welcome_softwarn   → render InvitationWelcome (the mismatch case)
 *   family_conflict               → render FamilyConflict
 *   already_member_redirect       → toast + /dashboard
 */
const InvitationRouter: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isAuthenticated, sessionToken, user } = useAuth();
  const { invitationToken, validateInvitation, acceptInvitation, clearInvitation } = useInvitation();
  // Token may arrive via React Router URL param (when routed from
  // /invite/:token), or via the InvitationContext (which restores from
  // localStorage / URL query). Use whichever is available.
  const routeToken = useParams<{ token?: string }>().token || null;
  const effectiveToken = invitationToken || routeToken;
  // Kick off validation immediately when we have a route-param token but
  // InvitationContext hasn't picked it up yet — this seeds localStorage
  // and the context state so downstream flows (SignUpPage banner, etc.)
  // have the invitation info.
  useEffect(() => {
    if (routeToken && !invitationToken) {
      localStorage.setItem('invitation_token', routeToken);
      validateInvitation(routeToken);
    }
  }, [routeToken, invitationToken, validateInvitation]);
  const [preflightLoading, setPreflightLoading] = useState(true);
  const [preflightError, setPreflightError] = useState<string | null>(null);
  // The destination we resolved — drives what we render. Null means
  // we're still loading or haven't decided yet.
  const [destination, setDestination] = useState<string | null>(null);
  const [familyConflictData, setFamilyConflictData] = useState<{
    currentFamilyName?: string;
    invitedFamilyName: string;
  } | null>(null);
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    if (!effectiveToken) {
      // No token to route — fall through. Either it's still loading from
      // localStorage / URL parse, or the user navigated here directly.
      // Wait one tick for the InvitationContext to pick it up.
      const wait = setTimeout(() => {
        if (!effectiveToken) {
          setPreflightLoading(false);
          setPreflightError('No invitation token');
        }
      }, 500);
      return () => clearTimeout(wait);
    }
    handled.current = true;
    (async () => {
      try {
        const result = await familyAPI.preflightInvitation(effectiveToken, sessionToken);
        if (!result.ok) {
          setPreflightError(result.error?.code || 'INVITATION_INVALID');
          setPreflightLoading(false);
          return;
        }
        const dest = result.destination!;
        const inv = result.invitation;
        const ctx = result.context;

        // ─── React on the destination ────────────────────────────────
        switch (dest) {
          case 'signup':
            // Push them to the signup page. Invitation context is already
            // in localStorage; SignUpPage reads it for the banner.
            navigate('/signup', { replace: true });
            break;

          case 'login':
          case 'login_for_existing_member':
          case 'login_then_family_conflict':
            navigate('/login', { replace: true });
            break;

          case 'auto_accept':
            // Signed in, email matches, no family. Accept silently and
            // route to quickstart — the invitee will pick their role +
            // athlete on the invited-combined screen. (The inviter has
            // no UI to set anticipated_user_type, so we never pre-pick
            // a role here.)
            if (sessionToken) {
              const accept = await acceptInvitation(sessionToken, {
                name: '',
                email: '',
                selected_user_type: null,
              });
              if (accept.ok) {
                toast.success(`Welcome to the ${inv?.family_name} family!`);
                // Don't clearInvitation here — the combined screen on the
                // next page reads invitationInfo to render the right
                // banner copy + role toggle context. OnboardingQuickStart
                // clears on submit.
                navigate('/onboarding/quickStart', { replace: true });
              } else {
                // Auto-accept failed mid-flight. Branch on the structured
                // code so messaging matches the failure: family conflict
                // gets the conflict surface; everything else (FAMILY_FULL,
                // INVITATION_EXPIRED, INVITATION_USED, generic) shows the
                // preflightError page with the specific code.
                if (accept.code === 'FAMILY_CONFLICT') {
                  setFamilyConflictData({
                    invitedFamilyName: inv?.family_name || 'this family',
                  });
                  setDestination('family_conflict');
                } else {
                  setPreflightError(accept.code || 'INVITATION_INVALID');
                }
              }
            } else {
              navigate('/login', { replace: true });
            }
            break;

          case 'already_member_redirect': {
            // Silent dedup. Two real cases:
            //   - Freshly-signed-up user bounced through preflight by
            //     the React state-batching race. onboarding_step < 100.
            //     Route to quickStart — and DO NOT clearInvitation here,
            //     so OnboardingQuickStart can render the combined screen
            //     (it reads invitationInfo for branching). The clear
            //     happens in quickStart's submit handler instead.
            //   - Existing complete member re-clicks their old invite.
            //     onboarding_step >= 100. Toast + dashboard, and clear
            //     here because there's no further use for the token.
            const u = user as { onboarding_step?: number } | null;
            const onboardingComplete = !!(u && typeof u.onboarding_step === 'number' && u.onboarding_step >= 100);
            if (onboardingComplete) {
              clearInvitation();
              toast.success(`You're already in the ${inv?.family_name} family.`);
              navigate('/dashboard', { replace: true });
            } else {
              // Preserve invitationInfo through the navigate.
              navigate('/onboarding/quickStart', { replace: true });
            }
            break;
          }

          case 'family_conflict':
            setFamilyConflictData({
              invitedFamilyName: inv?.family_name || 'this family',
            });
            setDestination('family_conflict');
            break;

          case 'invitation_welcome_softwarn':
            // Email mismatch — render the existing soft-warn surface.
            setDestination('invitation_welcome_softwarn');
            break;

          default:
            setPreflightError('Unknown destination');
        }
      } catch (err: any) {
        console.error('Invitation preflight failed:', err);
        setPreflightError(err?.message || 'Failed to load invitation');
      } finally {
        setPreflightLoading(false);
      }
    })();
  }, [effectiveToken, isAuthenticated, sessionToken]);

  if (preflightLoading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#fafbff', fontFamily: 'Manrope, sans-serif',
      }}>
        <p style={{ color: '#6B7280' }}>Loading invitation…</p>
      </div>
    );
  }

  if (preflightError) {
    const copy = (() => {
      switch (preflightError) {
        case 'INVITATION_EXPIRED':
          return {
            title: 'Invitation Expired',
            body: 'This invitation link has expired. Ask the inviter to send a fresh invite.',
          };
        case 'INVITATION_USED':
          return {
            title: 'Invitation Already Used',
            body: 'This invitation link has already been used. If that wasn\'t you, ask the inviter to send a fresh invite.',
          };
        case 'FAMILY_FULL':
          return {
            title: 'Family Is Full',
            body: 'This family has reached its member limit and can\'t accept new members right now. Ask the inviter to upgrade or remove an existing member.',
          };
        case 'MISSING_TOKEN':
          return {
            title: 'No Invitation Found',
            body: 'We couldn\'t find an invitation in this link.',
          };
        case 'INVITATION_INVALID':
        default:
          return {
            title: 'Invitation Unavailable',
            body: 'This invitation link is invalid, expired, or already used. If you expected to join a family, ask the inviter to send a fresh invite.',
          };
      }
    })();
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#fafbff', padding: 24, fontFamily: 'Manrope, sans-serif',
      }}>
        <div style={{
          maxWidth: 480, background: 'white', borderRadius: 16,
          padding: 32, textAlign: 'center',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#151133', marginTop: 0, marginBottom: 12 }}>
            {copy.title}
          </h1>
          <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 24, lineHeight: 1.6 }}>
            {copy.body}
          </p>
          <button
            onClick={() => { clearInvitation(); navigate('/login'); }}
            style={{
              padding: '12px 24px', background: '#F9550D', color: 'white',
              border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  if (destination === 'family_conflict' && familyConflictData) {
    return (
      <FamilyConflict
        invitedFamilyName={familyConflictData.invitedFamilyName}
        currentFamilyName={familyConflictData.currentFamilyName}
      />
    );
  }

  if (destination === 'invitation_welcome_softwarn') {
    return <InvitationWelcome />;
  }

  // Fallthrough — preflight already navigated us elsewhere.
  return null;
};

export default InvitationRouter;
